//=============================================================================
// Promotion Stacking - Edit
//=============================================================================

import React, {
    useState,
    useEffect,
    useContext,
  } from "react";
  
  import { useHistory, useLocation } from "react-router-dom";
  import { toast } from "react-toastify";
  
  import { styles } from "../Promotion/Criteria/styles";
  import StackingPromotion from "apis/StackingPromotion/StackingPromotion";
  import Context from "context/context";
  
  // Material UI
  import { withStyles } from "@material-ui/core/styles";
  import Grid from "@material-ui/core/Grid";
  import Button from "@material-ui/core/Button";
  import TextField from "@material-ui/core/TextField";
  import MenuItem from "@material-ui/core/MenuItem";
  import IconButton from "@material-ui/core/IconButton";
  import Checkbox from "@material-ui/core/Checkbox";
  import Paper from "@material-ui/core/Paper";
  import Typography from "@material-ui/core/Typography";
  import Divider from "@material-ui/core/Divider";
  import Chip from "@material-ui/core/Chip";
  import CircularProgress from "@material-ui/core/CircularProgress";
  import Box from "@material-ui/core/Box";
  
  // Icons
  import ArrowBackIcon from "@material-ui/icons/ArrowBack";
  import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
  import LayersIcon from "@material-ui/icons/Layers";
  import SearchIcon from "@material-ui/icons/Search";
  import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
  
  import { useTranslation } from "react-i18next";
  
  
  //=============================================================================
  // Rounded TextField
  //=============================================================================
  
  const CssTextField = withStyles({
    root: {
      "& .MuiFormLabel-root": { color: "#707070" },
      "& .MuiInputBase-input": { color: "#1a1a1a" },
      "& .MuiOutlinedInput-root": {
        borderRadius: 24,
        "& fieldset": { borderColor: "#D3D3D3" },
        "&:hover fieldset": { borderColor: "#9E9E9E" },
        "&.Mui-focused fieldset": { borderColor: "#305698" },
      },
      "& .MuiOutlinedInput-input": { padding: "14px 18px" },
      "& .MuiSelect-select": { padding: "14px 18px" },
    },
  })(TextField);
  
  
  const CATEGORY_OPTIONS = [
    { label: "Order id", value: "order_id", apiType: "Order_id" },
    { label: "Order line number", value: "order_line_number", apiType: "Order_line_number" },
  ];
  
  
  //=============================================================================
  // Component
  //=============================================================================
  
  export default function StackingPromoEdit() {
    const { dispatch } = useContext(Context);
  
    const history = useHistory();
    const location = useLocation();
    const classes = styles();
    const { t } = useTranslation();
  
    const { getStackDetail, updateStack } = StackingPromotion();
  
    // promotionId / type passed from the list screen's "View" action
    const promotionId = location?.state?.promotionId;
    const stackType = location?.state?.type;
  
  
    //===========================================================================
    // Form state
    //===========================================================================
  
    const [promotionName, setPromotionName] = useState("");
    const [promotionDescription, setPromotionDescription] = useState("");
    const [isActive, setIsActive] = useState(true);
  
  
    //===========================================================================
    // Member Promotion_Rule records
    //
    // Each item: { promotion_id, promotion_name, promotion_description, selected }
    // selected === true means allow_promotion_stacking = 1 for that promotion
    //===========================================================================
  
    const [promotionsList, setPromotionsList] = useState([]);
    const [initialSelectedIds, setInitialSelectedIds] = useState([]);
    const [loadingPromotions, setLoadingPromotions] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchValue, setSearchValue] = useState("");
  
  
    //===========================================================================
    // Breadcrumb
    //===========================================================================
  
    useEffect(() => {
      dispatch({
        type: "SET_PATHNAME",
        payload: ["HOME", "ADMIN", "Promotion Management", "Criteria", "Edit"],
      });
    }, [dispatch]);
  
  
    //===========================================================================
    // Load stack head + member promotions
    //===========================================================================
  
    useEffect(() => {
      if (!promotionId) {
        toast.error("Missing promotion to edit", { toastId: "edit-missing-id" });
        history.goBack();
        return;
      }
  
      loadStackDetail();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promotionId]);
  
    const loadStackDetail = async () => {
      try {
        setLoadingPromotions(true);
  
        const response = await getStackDetail({
          promotionId,
          type: stackType,
        });
  
        if (response?.success) {
          const head = response?.data?.head || {};
          const members = response?.data?.members || [];
  
          setPromotionName(head?.promotion_name || "");
          setPromotionDescription(head?.promotion_description || "");
          setIsActive(head?.is_active !== undefined ? !!head.is_active : true);
  
          const mapped = members.map((item) => ({
            ...item,
            selected: !!item.allow_promotion_stacking,
          }));
  
          setPromotionsList(mapped);
          setInitialSelectedIds(
            mapped.filter((m) => m.selected).map((m) => m.promotion_id)
          );
        } else {
          toast.error(response?.message || "Unable to load promotion stack", {
            toastId: "load-stack-detail-error",
          });
        }
      } catch (error) {
        console.error("loadStackDetail error:", error);
        toast.error("Unable to load promotion stack", { toastId: "load-stack-detail-error" });
      } finally {
        setLoadingPromotions(false);
      }
    };
  
  
    //===========================================================================
    // Toggle member promotion — keyed by promotion_id
    //===========================================================================
  
    const togglePromotionSelected = (promotionId) => {
      setPromotionsList((list) =>
        list.map((promotion) =>
          promotion.promotion_id === promotionId
            ? { ...promotion, selected: !promotion.selected }
            : promotion
        )
      );
    };
  
  
    //===========================================================================
    // Select all (visible/filtered rows only)
    //===========================================================================
  
    const handleSelectAll = () => {
      const visiblePromotions = getFilteredPromotions();
      const allSelected = visiblePromotions.every((promotion) => promotion.selected);
      const visibleIds = new Set(visiblePromotions.map((item) => item.promotion_id));
  
      setPromotionsList((list) =>
        list.map((promotion) =>
          visibleIds.has(promotion.promotion_id)
            ? { ...promotion, selected: !allSelected }
            : promotion
        )
      );
    };
  
  
    //===========================================================================
    // Filter
    //===========================================================================
  
    const getFilteredPromotions = () => {
      if (!searchValue.trim()) {
        return promotionsList;
      }
  
      const search = searchValue.toLowerCase();
  
      return promotionsList.filter((promotion) =>
        String(promotion?.promotion_name || "").toLowerCase().includes(search) ||
        String(promotion?.promotion_description || "").toLowerCase().includes(search)
      );
    };
  
  
    //===========================================================================
    // Selected promotions
    //===========================================================================
  
    const selectedPromotions = promotionsList.filter((promotion) => promotion.selected);
  
  
    //===========================================================================
    // Submit
    //
    // Sends the full current selection. The backend sets
    // allow_promotion_stacking = 1 for ids present in selectedPromotionIds,
    // and = 0 for any promotion of this type that was previously selected
    // (in initialSelectedIds) but is no longer selected.
    //===========================================================================
  
    const handleSubmit = async () => {
  
      if (!promotionName.trim()) {
        toast.error("Enter Promotion Name", { toastId: "promotion-name-required" });
        return;
      }
  
      if (!promotionDescription.trim()) {
        toast.error("Enter Promotion description", { toastId: "promotion-description-required" });
        return;
      }
  
      try {
        setSaving(true);
  
        const payload = {
          promotionId,
          type: stackType,
          isActive,
          promotionName: promotionName.trim(),
          promotionDescription: promotionDescription.trim(),
          selectedPromotionIds: selectedPromotions.map((promotion) => promotion.promotion_id),
          // sent so the backend can flip anything removed from the selection
          previouslySelectedPromotionIds: initialSelectedIds,
        };
  
        const response = await updateStack(payload);
  
        if (response?.success) {
          toast.success("Promotion stacking updated successfully", { toastId: "stack-updated" });
          history.goBack();
          return;
        }
  
        toast.error(response?.message || "Failed to update promotion stacking", {
          toastId: "stack-update-failed",
        });
  
      } catch (error) {
        console.error("updateStack error:", error);
        toast.error("Failed to update promotion stacking", { toastId: "stack-update-error" });
      } finally {
        setSaving(false);
      }
    };
  
  
    //===========================================================================
    // Current visible list
    //===========================================================================
  
    const filteredPromotions = getFilteredPromotions();
  
    const allVisibleSelected =
      filteredPromotions.length > 0 &&
      filteredPromotions.every((promotion) => promotion.selected);
  
    const categoryLabel =
      CATEGORY_OPTIONS.find((item) => item.apiType === stackType)?.label || stackType || "";
  
  
    //===========================================================================
    // UI
    //===========================================================================
  
    return (
      <React.Fragment>
        <div className={classes.mainDiv} style={{ paddingBottom: 30 }}>
  
          {/* Header */}
          <Grid container alignItems="center" style={{ padding: "12px 18px 8px" }}>
            <Grid item>
              <IconButton onClick={() => history.goBack()} size="small">
                <ArrowBackIcon style={{ color: "#305698" }} />
              </IconButton>
            </Grid>
            <Grid item>
              <Typography style={{ fontSize: 20, fontWeight: 600, color: "#202020" }}>
                {t("Edit Promotion Stack")}
              </Typography>
            </Grid>
          </Grid>
  
  
          {/* Main form card */}
          <Paper
            elevation={0}
            style={{ margin: "8px 18px 0", border: "1px solid #E1E5EA", borderRadius: 18, padding: 20 }}
          >
            <Grid container spacing={2}>
  
              <Grid item xs={12} sm={6} md={3}>
                <CssTextField
                  id="PromotionName"
                  label={t("Promotion Name")}
                  variant="outlined"
                  fullWidth
                  required
                  value={promotionName}
                  onChange={(event) => setPromotionName(event.target.value)}
                />
              </Grid>
  
              <Grid item xs={12} sm={6} md={3}>
                <CssTextField
                  id="PromotionDescription"
                  label={t("Promotion description")}
                  variant="outlined"
                  fullWidth
                  required
                  value={promotionDescription}
                  onChange={(event) => setPromotionDescription(event.target.value)}
                />
              </Grid>
  
              <Grid item xs={12} sm={6} md={3}>
                <CssTextField
                  id="Category"
                  label={t("Category")}
                  variant="outlined"
                  fullWidth
                  disabled
                  value={categoryLabel}
                />
              </Grid>
  
              <Grid item xs={12} sm={6} md={3}>
                <CssTextField
                  id="Status"
                  label={t("Status")}
                  variant="outlined"
                  select
                  fullWidth
                  required
                  value={isActive}
                  onChange={(event) =>
                    setIsActive(event.target.value === "true" || event.target.value === true)
                  }
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </CssTextField>
              </Grid>
  
            </Grid>
  
            <Box
              style={{
                marginTop: 18,
                padding: "12px 16px",
                borderRadius: 12,
                background: "#F4F8FC",
                border: "1px solid #D9E6F4",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <InfoOutlinedIcon style={{ color: "#305698", fontSize: 20 }} />
              <Typography style={{ fontSize: 13, color: "#4D5B6A" }}>
                Check a promotion to include it in this stack (sets allow stacking to 1).
                Uncheck it to remove it from the stack (sets allow stacking to 0).
              </Typography>
            </Box>
          </Paper>
  
  
          {/* Promotions section */}
          <Paper
            elevation={0}
            style={{ margin: "18px 18px 0", border: "1px solid #E1E5EA", borderRadius: 18, overflow: "hidden" }}
          >
            <Box style={{ padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Box style={{ width: 40, height: 40, borderRadius: 12, background: "#EAF2FB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LayersIcon style={{ color: "#305698" }} />
                </Box>
                <Box>
                  <Typography style={{ fontSize: 17, fontWeight: 600, color: "#202020" }}>
                    Promotions in this stack
                  </Typography>
                  <Typography style={{ fontSize: 13, color: "#777", marginTop: 3 }}>
                    {categoryLabel} promotions from Promotion Rule
                  </Typography>
                </Box>
              </Box>
  
              <Chip
                label={`${selectedPromotions.length} selected`}
                style={{ background: "#EAF2FB", color: "#305698", fontWeight: 500 }}
              />
            </Box>
  
            <Divider />
  
            <Box style={{ padding: "14px 22px", display: "flex", alignItems: "center", gap: 15 }}>
              <CssTextField
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search promotion"
                variant="outlined"
                size="small"
                style={{ maxWidth: 350 }}
                InputProps={{
                  startAdornment: <SearchIcon style={{ color: "#888", marginRight: 8, fontSize: 20 }} />,
                }}
              />
  
              <Button
                variant="text"
                color="primary"
                onClick={handleSelectAll}
                disabled={loadingPromotions || filteredPromotions.length === 0}
              >
                {allVisibleSelected ? "Unselect all" : "Select all"}
              </Button>
            </Box>
  
            <Divider />
  
            {loadingPromotions && (
              <Box style={{ padding: 40, textAlign: "center" }}>
                <CircularProgress size={28} style={{ color: "#305698" }} />
                <Typography style={{ marginTop: 12, fontSize: 13, color: "#777" }}>
                  Loading promotions...
                </Typography>
              </Box>
            )}
  
            {!loadingPromotions && filteredPromotions.length === 0 && (
              <Box style={{ padding: 40, textAlign: "center" }}>
                <Typography style={{ fontSize: 14, color: "#777" }}>
                  No available promotions found for {categoryLabel}.
                </Typography>
              </Box>
            )}
  
            {!loadingPromotions && filteredPromotions.length > 0 && (
              <Box style={{ maxHeight: 400, overflowY: "auto" }}>
                {filteredPromotions.map((promotion, index) => (
                  <Box
                    key={promotion.promotion_id}
                    onClick={() => togglePromotionSelected(promotion.promotion_id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "13px 22px",
                      cursor: "pointer",
                      background: promotion.selected ? "#F5F9FD" : "#FFFFFF",
                      borderBottom: index !== filteredPromotions.length - 1 ? "1px solid #EEEEEE" : "none",
                    }}
                  >
                    <Checkbox
                      checked={!!promotion.selected}
                      onChange={() => togglePromotionSelected(promotion.promotion_id)}
                      color="primary"
                      onClick={(event) => event.stopPropagation()}
                    />
  
                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: 14, fontWeight: 600, color: "#252525" }}>
                        {promotion.promotion_name}
                      </Typography>
                      <Typography style={{ fontSize: 13, color: "#777", marginTop: 3 }}>
                        {promotion.promotion_description || "-"}
                      </Typography>
                    </Box>
  
                    {promotion.selected && (
                      <CheckCircleOutlineIcon style={{ color: "#305698", fontSize: 20 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
  
  
          {/* Submit */}
          <Grid container style={{ padding: "20px 18px" }} justifyContent="flex-end">
            <Button
              variant="contained"
              className={classes.submitBtn}
              onClick={handleSubmit}
              disabled={saving || loadingPromotions}
              style={{
                minWidth: 130,
                height: 48,
                borderRadius: 10,
                background: "#305698",
                textTransform: "none",
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {saving ? <CircularProgress size={22} style={{ color: "#FFFFFF" }} /> : "Save Changes"}
            </Button>
          </Grid>
  
        </div>
      </React.Fragment>
    );
  }